import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { NgPipesModule } from 'angular-pipes';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TabNavigationService } from './../form-application/mortgage-application/services/tab-navigation.service';
import { RomeKendoModule } from './../rome-kendo/rome-kendo.module';
import { AccordionItemComponent } from './components/accordion/accordion-item/accordion-item.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { AffordabilityResultsComponent } from './components/affordability-results/affordability-results.component';
import { CaseLayoutContainerComponent } from './components/case-layout-container/case-layout-container.component';
import { CaseMessageEditorComponent } from './components/case-message-editor/case-message-editor.component';
import { CaseRequestStatusHistoryComponent } from './components/case-request-status-history/case-request-status-history.component';
import { CaseRequestsComponent } from './components/case-requests/case-requests.component';
import { CaseSummaryHeaderComponent } from './components/case-summary-header/case-summary-header.component';
import { ContextualHelpContainerComponent } from './components/contextual-help-container/contextual-help-container.component';
import { ContextualHelpDisplayerComponent } from './components/contextual-help-displayer/contextual-help-displayer.component';
import { DateInputComponent } from './components/date-input/date-input.component';
import { AdviceNotesDialogComponent } from './components/dialogs/advice-notes-dialog/advice-notes-dialog.component';
import { AffordabilityResultsDialogComponent } from './components/dialogs/affordability-results-dialog/affordability-results-dialog.component';
import { AffordabilityWarningDialogComponent } from './components/dialogs/affordability-warning-dialog/affordability-warning-dialog.component';
import { ConfirmationDialogComponent } from './components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ProductSpecialFeaturesDialogComponent } from './components/dialogs/product-special-features-dialog/product-special-features-dialog.component';
import { SessionAboutToExpireDialogComponent } from './components/dialogs/session-about-to-expire-dialog/session-about-to-expire-dialog.component';
import { LinkedCaseDetailsComponent } from './components/linked-case-details/linked-case-details.component';
import { MainLayoutComponent } from './components/main-layout/main-layout.component';
import { MessageResultComponent } from './components/message-result/message-result.component';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { ProgressStepperItemComponent } from './components/progress-stepper-item/progress-stepper-item.component';
import { SavedSearchesComponent } from './components/saved-searches/saved-searches.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { ValidationErrorGenericComponent } from './components/validation-error-generic/validation-error-generic.component';
import { ApplicationHostTypeDirective } from './directives/application-host-type.directive';
import { BlurOnCloseDirective } from './directives/blur-on-close.directive';
import { CategorizeAccountCustomerStatusDirective } from './directives/categorize-account-customer-status.directive';
import { CategorizeCaseStatusDirective } from './directives/categorize-case-status.directive';
import { CategorizeOfferStatusDirective } from './directives/categorize-offer-status.directive';
import { CategorizeRecommendationStatusDirective } from './directives/categorize-recommendation-status.directive';
import { CategorizeTransactionIconDirective } from './directives/categorize-transaction-icon.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { DisableNumericInputIncrementationDirective } from './directives/disable-numerictextbox-incrementation.directive';
import { OnlyDigitsDirective } from './directives/only-digits.directive';
import { PermissionCheckerDirective } from './directives/permission-checker.directive';
import { RequiredFormFieldDirective } from './directives/required-form-field.directive';
import { StickyRightPanelDirective } from './directives/sticky-right-panel.directive';
import { FirstKeyPipe } from './pipes/first-key.pipe';
import {
  FormatNewLinesForInnerHtml,
  FormatStringPipe,
  RemoveWhiteSpaces,
  ReplaceWithNewLine
} from './pipes/format-string.pipe';
import { LocalizeErrorMessagePipe } from './pipes/localize-error-message.pipe';
import { LocalizeLabelPipe } from './pipes/localize-label.pipe';
import { LocalizeOptionListFilteredByAttributePipe } from './pipes/localize-option-list-filtered-by-attribute.pipe';
import { LocalizeOptionListPipe } from './pipes/localize-option-list.pipe';
import { LocalizeOptionPipe } from './pipes/localize-option.pipe';
import { LocalizeTooltipPipe } from './pipes/localize-tooltip.pipe';
import { MessageContentAsHtmlPipe } from './pipes/message-content-as-html.pipe';
import { ToTrustedHtmlPipe } from './pipes/to-trusted-html.pipe';
import { AddressesService } from './services/addresses.service';
import { AdviceNotesService } from './services/advice-notes.service';
import { AgentsBusinessRelationService } from './services/agents-business-relation.service';
import { BaseCaseHubService } from './services/base-case-hub.service';
import { CaseRequestsService } from './services/case-requests.service';
import { ContextualHelpNotifierService } from './services/contextual-help-notifier.service';
import { DeserializableObjectsService } from './services/deserializable-objects.service';
import { WorldPayUrlService } from './services/world-pay-url.service';
import { MustMatchDirective } from './validators/must-match.directive';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';

const COMPONENTS = [
  MainLayoutComponent,
  LinkedCaseDetailsComponent,
  DateInputComponent,
  ContextualHelpDisplayerComponent,
  ContextualHelpContainerComponent,
  CaseMessageEditorComponent,
  MessageResultComponent,
  SavedSearchesComponent,
  ConfirmationDialogComponent,
  ProductSpecialFeaturesDialogComponent,
  PdfViewerComponent,
  CaseLayoutContainerComponent,
  CaseSummaryHeaderComponent,
  AdviceNotesDialogComponent,
  ValidationErrorGenericComponent,
  TooltipComponent,
  ProgressStepperItemComponent,
  CaseRequestsComponent,
  CaseRequestStatusHistoryComponent,
  AffordabilityResultsComponent,
  AffordabilityResultsDialogComponent,
  AffordabilityWarningDialogComponent,
  AccordionComponent,
  AccordionItemComponent,
  SessionAboutToExpireDialogComponent,
  PasswordStrengthComponent
];
const PIPES = [
  LocalizeLabelPipe,
  LocalizeOptionListPipe,
  LocalizeOptionListFilteredByAttributePipe,
  LocalizeOptionPipe,
  LocalizeTooltipPipe,
  FormatStringPipe,
  RemoveWhiteSpaces,
  ReplaceWithNewLine,
  FormatNewLinesForInnerHtml,
  MessageContentAsHtmlPipe,
  ToTrustedHtmlPipe,
  LocalizeErrorMessagePipe,
  FirstKeyPipe
];
const DIRECTIVES = [
  ApplicationHostTypeDirective,
  BlurOnCloseDirective,
  CategorizeAccountCustomerStatusDirective,
  CategorizeCaseStatusDirective,
  CategorizeOfferStatusDirective,
  CategorizeRecommendationStatusDirective,
  CategorizeTransactionIconDirective,
  ClickOutsideDirective,
  MustMatchDirective,
  OnlyDigitsDirective,
  PermissionCheckerDirective,
  RequiredFormFieldDirective,
  StickyRightPanelDirective,
  DisableNumericInputIncrementationDirective
];

@NgModule({
  imports: [RouterModule, FormsModule, InputsModule, RomeKendoModule, CommonModule, PdfViewerModule, NgPipesModule],
  declarations: [COMPONENTS, PIPES, DIRECTIVES],
  exports: [COMPONENTS, PIPES, DIRECTIVES, RouterModule, PdfViewerModule, NgPipesModule],
  providers: [
    BaseCaseHubService,
    AdviceNotesService,
    CaseRequestsService,
    TabNavigationService,
    DeserializableObjectsService,
    AddressesService,
    AgentsBusinessRelationService,
    WorldPayUrlService
  ]
})
export class RomeLibraryModule {}
